import { memo } from 'react';
import PropTypes from 'prop-types';

import StepperStep from './StepperStep.jsx';

const STATES = ['completed', 'current', 'upcoming'];

function normalizeState(state) {
  return STATES.includes(state) ? state : 'upcoming';
}

/**
 * Horizontal step indicator for a multi-step flow (a create wizard, a builder).
 * Design-system "Stepper": a numbered badge per step — a check once it is
 * completed — joined by chevrons.
 *
 * The component only **shows** progress. The flow owns the rules (what is valid,
 * which steps may be reopened) and passes each step's `state`; the stepper
 * never decides that a step is reachable.
 *
 * Opening steps:
 * - a **completed** step is a button that calls `onStepClick(id)`;
 * - the **current** step is never a button;
 * - an **upcoming** step is blocked by default, so it is plain text and takes no
 *   focus — pass `disabled: false` to let the flow jump ahead to it;
 * - any step with `disabled: true` is blocked. Give it a `disabledReason` and it
 *   is read aloud and shown as a tooltip.
 *
 * Without `onStepClick` nothing is a button.
 *
 * @param {object} props
 * @param {Array<{
 *   id: string,
 *   label: import('react').ReactNode,
 *   state: 'completed'|'current'|'upcoming',
 *   disabled?: boolean,
 *   disabledReason?: string,
 * }>} props.steps — In display order. `state` is required; `disabled` defaults to `true` for upcoming steps and `false` otherwise.
 * @param {(id: string) => void} [props.onStepClick] — Called with the id of the step the user opens. Pass a stable callback (`useCallback`): each step is memoized and skips re-rendering only while this reference is unchanged.
 * @param {string} [props.ariaLabel] — Accessible name of the navigation landmark.
 * @param {string} [props.className] — Classes for the `<nav>`.
 */
function Stepper({ steps, onStepClick, ariaLabel, className = '' }) {
  const items = steps.map((step, index) => {
    const state = normalizeState(step.state);
    const blocked = state !== 'current' && (step.disabled ?? state === 'upcoming');
    return {
      id: step.id,
      index,
      state,
      label: step.label,
      blocked,
      reason: step.disabledReason,
      interactive: Boolean(onStepClick) && state !== 'current' && !blocked,
    };
  });

  return (
    <nav aria-label={ariaLabel} className={className || undefined}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-x-2 gap-y-2 p-0">
        {items.map((item) => (
          <StepperStep
            key={item.id}
            stepId={item.id}
            index={item.index}
            state={item.state}
            label={item.label}
            isLast={item.index === items.length - 1}
            interactive={item.interactive}
            blocked={item.blocked}
            reason={item.reason}
            onSelect={item.interactive ? onStepClick : undefined}
          />
        ))}
      </ol>
    </nav>
  );
}

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      state: PropTypes.oneOf(STATES).isRequired,
      disabled: PropTypes.bool,
      disabledReason: PropTypes.string,
    }),
  ).isRequired,
  onStepClick: PropTypes.func,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default memo(Stepper);
